import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../scripts/AuthContext";
import { useSocialDmSocket } from "../scripts/SocialDmSocketContext";
import {
  listSocialDmMessages,
  markSocialDmRead,
  sendSocialDmMessage,
} from "@/lib/socialDmApi";

function upsertMessage(prev, message) {
  if (!message?.message_id) return prev;
  const idx = prev.findIndex((m) => m.message_id === message.message_id);
  if (idx === -1) return [...prev, message];
  const next = [...prev];
  next[idx] = message;
  return next;
}

export function useSocialDmThread(conversationId) {
  const { accessToken } = useAuth();
  const { subscribe } = useSocialDmSocket();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [peerLastReadAt, setPeerLastReadAt] = useState(null);
  const markedReadRef = useRef(null);
  const loadingOlderRef = useRef(false);

  const currentUserId = useMemo(() => {
    if (!accessToken) return null;
    try {
      return jwtDecode(accessToken)?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  const refresh = useCallback(async () => {
    if (!accessToken || !conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setHasMoreOlder(true);
    setPeerLastReadAt(null);
    try {
      const data = await listSocialDmMessages(
        accessToken,
        conversationId,
        { limit: 50 }
      );
      setMessages(data.messages || []);
      setHasMoreOlder((data.messages || []).length >= 50);
      if (data.peer_last_read_at !== undefined) {
        setPeerLastReadAt(data.peer_last_read_at);
      }
    } catch (err) {
      console.error("Failed to load social DM messages:", err);
      setError(err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, conversationId]);

  useEffect(() => {
    markedReadRef.current = null;
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!accessToken || !conversationId) return;
    if (markedReadRef.current === conversationId) return;
    markedReadRef.current = conversationId;
    markSocialDmRead(accessToken, conversationId).catch((err) => {
      console.error("Failed to mark social DM read:", err);
      markedReadRef.current = null;
    });
  }, [accessToken, conversationId, messages.length]);

  useEffect(() => {
    return subscribe((event, payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (event === "social-dm.message") {
        const msg =
          payload?.message ?? (payload?.message_id ? payload : null);
        if (msg) setMessages((prev) => upsertMessage(prev, msg));
      }
      if (event === "social-dm.read") {
        if (
          payload?.user_id &&
          currentUserId &&
          String(payload.user_id) !== String(currentUserId)
        ) {
          setPeerLastReadAt(payload.last_read_at || null);
        }
      }
    });
  }, [subscribe, conversationId, currentUserId]);

  const loadOlder = useCallback(async () => {
    if (
      !accessToken ||
      !conversationId ||
      loadingOlderRef.current ||
      !hasMoreOlder ||
      !messages.length
    ) {
      return;
    }
    loadingOlderRef.current = true;
    try {
      const beforeId = messages[0]?.message_id;
      const { messages: batch } = await listSocialDmMessages(
        accessToken,
        conversationId,
        { limit: 50, beforeId }
      );
      if (!batch?.length) {
        setHasMoreOlder(false);
        return;
      }
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.message_id));
        const older = batch.filter((m) => !ids.has(m.message_id));
        return [...older, ...prev];
      });
      setHasMoreOlder(batch.length >= 50);
    } catch (err) {
      console.error("Failed to load older social messages:", err);
    } finally {
      loadingOlderRef.current = false;
    }
  }, [accessToken, conversationId, hasMoreOlder, messages]);

  const sendMessage = useCallback(
    async (body) => {
      if (!accessToken || !conversationId) return null;
      setSending(true);
      try {
        const msg = await sendSocialDmMessage(
          accessToken,
          conversationId,
          body
        );
        if (msg) setMessages((prev) => upsertMessage(prev, msg));
        return msg;
      } catch (err) {
        console.error("Failed to send social DM:", err);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [accessToken, conversationId]
  );

  return {
    messages,
    loading,
    sending,
    error,
    hasMoreOlder,
    currentUserId,
    peerLastReadAt,
    refresh,
    loadOlder,
    sendMessage,
    setMessages,
  };
}
